/**
 * React Router v7 configuration.
 *
 * Route tree:
 *   /                 → redirect: to /dashboard if authenticated, else /login
 *
 *   /login            | public (RequireGuest)
 *   /register         | public (RequireGuest)
 *   /forgot-password  | public (RequireGuest)
 *   /reset-password   | public (token in query)
 *   /verify-email     | public (token in query)
 *   /mfa/reset-request | public
 *   /mfa/reset-cancel  | public (token in query)
 *   /mfa/reset-confirm | public (token in query)
 *
 *   /mfa/setup        | RequireAuth (pre-MFA OK)
 *   /mfa/verify       | RequireAuth (pre-MFA OK — this IS the MFA page)
 *
 *   /dashboard        | RequireMfa
 *   /<23 placeholders>| RequireMfa, render PlaceholderPage with config
 *
 *   *                 | NotFound
 *
 * Phase 4.2 Bucata 2.A.bis2: every sidebar nav item has a corresponding
 * route now. Items not yet implemented render PlaceholderPage with their
 * phase tag and feature list — so the user can browse the full surface of
 * the platform and see what's coming.
 *
 * As real pages ship per phase, swap `PlaceholderPage` for the real
 * component and remove the entry from `placeholderConfig`.
 */

import { lazy } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useRouteError,
  isRouteErrorResponse,
} from "react-router";
import PublicLayout from "@/layouts/PublicLayout";
import AppLayout from "@/layouts/AppLayout";
import { RequireAuth, RequireGuest, RequireMfa } from "@/lib/auth/guards";

// Auth pages
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import TeamAcceptPage from "@/pages/auth/TeamAcceptPage"; // SUB-USERS SU2a: public, code in query
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import ImpersonationConsentPage from "@/pages/ImpersonationConsentPage";
import VerifyEmailPage from "@/pages/auth/VerifyEmailPage";
import VerifyEmailChangePage from "./pages/auth/VerifyEmailChangePage";
import MfaSetupPage from "@/pages/auth/MfaSetupPage";
import MfaEnrollPage from "@/pages/auth/MfaEnrollPage"; // MFA-LINK-1b
import MfaVerifyPage from "@/pages/auth/MfaVerifyPage";
import MfaResetRequestPage from "@/pages/auth/MfaResetRequestPage";
import MfaResetCancelPage from "@/pages/auth/MfaResetCancelPage";
import MfaResetConfirmPage from "@/pages/auth/MfaResetConfirmPage";

// App pages
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const ComposePage = lazy(() => import("@/pages/ComposePage"));
const AdminPricingPage = lazy(() => import("@/pages/admin/AdminPricingPage"));
const AdminUsersPage = lazy(() => import("@/pages/admin/AdminUsersPage"));
const AdminHealthPage = lazy(() => import("@/pages/admin/AdminHealthPage"));
const AdminInvitesPage = lazy(() => import("@/pages/admin/AdminInvitesPage"));
const AdminSendersPage = lazy(() => import("@/pages/admin/AdminSendersPage"));
const AdminActivityPage = lazy(() => import("@/pages/admin/AdminActivityPage"));
const GuideViewerPage = lazy(() => import("@/pages/GuideViewerPage"));
const TemplatesPage = lazy(() => import("@/pages/TemplatesPage"));
const AttachmentsPage = lazy(() => import("@/pages/AttachmentsPage"));
const ShortUrlPage = lazy(() => import("@/pages/ShortUrlPage"));
const QrPage = lazy(() => import("@/pages/QrPage"));
const MnpPage = lazy(() => import("@/pages/MnpPage"));
const ApiKeysPage = lazy(() => import("@/pages/ApiKeysPage"));
const WebhooksPage = lazy(() => import("@/pages/WebhooksPage"));
const Sms2mailPage = lazy(() => import("@/pages/Sms2mailPage"));
const Mail2smsPage = lazy(() => import("@/pages/Mail2smsPage"));
const PlaygroundPage = lazy(() => import("@/pages/PlaygroundPage"));
const OtpPage = lazy(() => import("@/pages/OtpPage"));
const MessagesPage = lazy(() => import("@/pages/MessagesPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const InboxPage = lazy(() => import("@/pages/InboxPage"));
const CampaignsPage = lazy(() => import("@/pages/CampaignsPage"));
const CampaignCreatePage = lazy(() => import("@/pages/CampaignCreatePage"));
const CampaignDetailPage = lazy(() =>
  import("@/pages/CampaignDetailPage").then((m) => ({
    default: m.CampaignDetailPage,
  })),
);
const ContactsPage = lazy(() => import("@/pages/ContactsPage"));
const BlocklistPage = lazy(() => import("@/pages/BlocklistPage"));
const ContactFieldsPage = lazy(() => import("@/pages/ContactFieldsPage"));
const ContactImportPage = lazy(() => import("@/pages/ContactImportPage"));
// WhatsApp Business channel (WA workstream)
const WhatsAppAccountsPage = lazy(
  () => import("@/pages/whatsapp/WhatsAppAccountsPage"),
);
const WhatsAppTemplatesPage = lazy(
  () => import("@/pages/whatsapp/WhatsAppTemplatesPage"),
);
// Channels & Senders — hosts the WhatsApp tabs in the senders nav slot
const ChannelsSendersPage = lazy(() => import("@/pages/ChannelsSendersPage"));
// Hub wallet — the authoritative balance (WA.P2.c)
const WalletPage = lazy(() => import("@/pages/WalletPage"));
// Invoices — real page replaces the placeholder (Phase 4.7 graduation)
const InvoicesPage = lazy(() => import("@/pages/InvoicesPage"));
// Your prices -- every price the account pays, per service (YOUR-PRICES-3)
const PricesPage = lazy(() => import("@/pages/PricesPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const HelpPage = lazy(() => import("@/pages/HelpPage"));
import NotFoundPage from "@/pages/NotFoundPage";
import PlaceholderPage from "@/pages/PlaceholderPage";
import { PLACEHOLDERS } from "@/pages/placeholderConfig";

// -----------------------------------------------------------
// Error boundary component
// -----------------------------------------------------------

function RootErrorBoundary() {
  const error = useRouteError();
  const status = isRouteErrorResponse(error) ? error.status : 500;
  const message = isRouteErrorResponse(error)
    ? error.statusText || "Error"
    : error instanceof Error
      ? error.message
      : "Unexpected error";

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-base p-6">
      <div className="max-w-md space-y-4 text-center">
        <div className="text-display-md font-semibold text-ink">{status}</div>
        <p className="text-body text-ink-muted">{message}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-pill bg-brand-500 px-6 py-2 text-body font-medium text-white transition-colors hover:bg-brand-600 focus:outline-none focus:shadow-focus"
        >
          Reload
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------
// Router definition
// -----------------------------------------------------------

export const router = createBrowserRouter([
  {
    path: "/",
    errorElement: <RootErrorBoundary />,
    children: [
      // Root: let the splash / guards decide; redirect to /dashboard as a hint
      { index: true, element: <Navigate to="/dashboard" replace /> },

      // -------------------------------------------------
      // Public routes
      // -------------------------------------------------
      {
        element: <PublicLayout />,
        children: [
          {
            path: "login",
            element: (
              <RequireGuest>
                <LoginPage />
              </RequireGuest>
            ),
          },
          {
            path: "register",
            element: (
              <RequireGuest>
                <RegisterPage />
              </RequireGuest>
            ),
          },
          {
            // SUB-USERS SU2a (18-Oct-2026): a member accepts an owner's invitation (code in query, generic negative).
            path: "team/accept",
            element: (
              <RequireGuest>
                <TeamAcceptPage />
              </RequireGuest>
            ),
          },
          {
            path: "forgot-password",
            element: (
              <RequireGuest>
                <ForgotPasswordPage />
              </RequireGuest>
            ),
          },
          { path: "reset-password", element: <ResetPasswordPage /> },
          { path: "impersonation-consent", element: <ImpersonationConsentPage /> },
          { path: "verify-email", element: <VerifyEmailPage /> },
          {
            path: "verify-email-change",
            element: <VerifyEmailChangePage />,
          },

          // MFA flows that need an authenticated (maybe pre-MFA) session
          {
            path: "mfa/setup",
            element: (
              <RequireAuth>
                <MfaSetupPage />
              </RequireAuth>
            ),
          },
          {
            path: "mfa/verify",
            element: (
              <RequireAuth>
                <MfaVerifyPage />
              </RequireAuth>
            ),
          },

          // MFA reset flow (public — user can't log in!)
          { path: "mfa/reset-request", element: <MfaResetRequestPage /> },
          { path: "mfa/reset-cancel", element: <MfaResetCancelPage /> },
          { path: "mfa/reset-confirm", element: <MfaResetConfirmPage /> },
          { path: "mfa/enroll", element: <MfaEnrollPage /> }, // MFA-LINK-1b: public (admin-issued token in query)
        ],
      },

      // -------------------------------------------------
      // Authenticated app (sidebar + header)
      // -------------------------------------------------
      {
        element: (
          <RequireMfa>
            <AppLayout />
          </RequireMfa>
        ),
        children: [
          { path: "dashboard", element: <DashboardPage /> },
          { path: "send", element: <ComposePage /> },
          { path: "templates", element: <TemplatesPage /> },
          { path: "attachments", element: <AttachmentsPage /> },
          { path: "short-url", element: <ShortUrlPage /> },
          { path: "qr", element: <QrPage /> },
          // MNP -- real page replaces the placeholder (MNP-1).
          { path: "mnp", element: <MnpPage /> },
            // API keys -- real page replaces the placeholder (API-1 step 4).
            { path: "api-keys", element: <ApiKeysPage /> },
            { path: "webhooks", element: <WebhooksPage /> },
            // SMS -> E-mail -- real page replaces the placeholder (sms2mail S3a, docs/sms2mail-design.md).
            { path: "sms2mail", element: <Sms2mailPage /> },
            // E-mail -> SMS -- real page replaces the placeholder (mail2sms M1, docs/mail2sms-design.md).
            { path: "mail2sms", element: <Mail2smsPage /> },
            // Playground -- real page replaces the placeholder (API-1 step 7).
            { path: "api-playground", element: <PlaygroundPage /> },
          // OTP/2FA -- settings + statistics; the module itself is API-only.
          { path: "otp", element: <OtpPage /> },
          { path: "messages", element: <MessagesPage /> },
          { path: "reports", element: <ReportsPage /> },
          { path: "inbox", element: <InboxPage /> },
          { path: "campaigns", element: <CampaignsPage /> },
          { path: "campaigns/new", element: <CampaignCreatePage /> },
          { path: "campaigns/:id/edit", element: <CampaignCreatePage /> },
          { path: "campaigns/:id", element: <CampaignDetailPage /> },
          { path: "contacts/fields", element: <ContactFieldsPage /> },
          { path: "contacts/import", element: <ContactImportPage /> },
          { path: "contacts", element: <ContactsPage /> },
          { path: "blocklist", element: <BlocklistPage /> },
          // Channels & Senders — WhatsApp lives here (owner decision);
          // the /whatsapp deep links stay valid.
          { path: "senders", element: <ChannelsSendersPage /> },
          { path: "whatsapp", element: <WhatsAppAccountsPage /> },
          { path: "whatsapp/templates", element: <WhatsAppTemplatesPage /> },
          // Hub wallet — real page replaces the placeholder (WA.P2.c)
          { path: "wallet", element: <WalletPage /> },
          { path: "invoices", element: <InvoicesPage /> },
          { path: "prices", element: <PricesPage /> },
          { path: "settings", element: <SettingsPage /> },
          { path: "help", element: <HelpPage /> },
          { path: "admin/users", element: <AdminUsersPage /> },
          { path: "admin/invites", element: <AdminInvitesPage /> },
          { path: "admin/pricing/:userId", element: <AdminPricingPage /> },
          { path: "admin/senders", element: <AdminSendersPage /> },
          { path: "admin/activity", element: <AdminActivityPage /> },
          { path: "admin/health", element: <AdminHealthPage /> },
          { path: "help/guides/:slug", element: <GuideViewerPage /> },
          // 23 placeholder routes generated from the config map.
          // Each one renders <PlaceholderPage /> with its own phase/feature list.
          ...PLACEHOLDERS.map((cfg) => ({
            path: cfg.path,
            element: (
              <PlaceholderPage
                icon={cfg.icon}
                titleKey={cfg.titleKey}
                phase={cfg.phase}
                descriptionKey={cfg.descriptionKey}
                featureKeys={cfg.featureKeys}
              />
            ),
          })),
        ],
      },

      // -------------------------------------------------
      // Catch-all
      // -------------------------------------------------
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);

/** Wrapper that just renders an Outlet — used if you ever want a bare root. */
export function RootOutlet() {
  return <Outlet />;
}
